/**
 * blueprintRoutes.tsx — page-wrapper components for Blueprints sibling routes.
 *
 * ## Why this file exists
 *
 * `CustomFieldsRegistry` and `AuthoringGuide` are inner components that do NOT
 * render their own `<FullPage>` or `<BlueprintsHeader>`. They need a thin wrapper
 * to provide full-page chrome before being mounted as direct `<Route element>`
 * values inside App.tsx's `<FlatRoutes>`.
 *
 * `TasksListPage` and `ActionsCatalogPage` already render `<FullPage>` +
 * `<BlueprintsHeader>` themselves — they are mounted directly (no wrapper here).
 *
 * ## Routing architecture (as of fix for routable-extension regression)
 *
 * `<ScaffolderPage>` MUST be a direct child of a `<Route>` inside `<FlatRoutes>`
 * so that Backstage's route-discovery traversal can find
 * `routeRef{type=absolute,id=scaffolder}`. The previous BlueprintsPage wrapper
 * (now deleted) buried `<ScaffolderPage>` inside an inner `<Routes>`, breaking
 * discovery. The fix uses sibling routes in App.tsx:
 *
 *   /create/tasks         → TasksListPage        (custom, full chrome)
 *   /create/actions       → ActionsCatalogPage   (custom, full chrome)
 *   /create/custom-fields → CustomFieldsPage     (wrapper in this file)
 *   /create/guide         → AuthoringGuidePage   (wrapper in this file)
 *   /create               → BlueprintsTemplatesTab → ScaffolderPage
 *                              (routable extension, direct FlatRoutes child)
 *
 * React Router v6 specificity ensures the static sibling paths always win over
 * ScaffolderPage's internal wildcard matching. `<FlatRoutes>` rewrites each
 * route path to `${path}/*`, so `/create/tasks` also matches
 * `/create/tasks/:taskId`; TasksListPage owns that detail route internally and
 * renders the OOTB scaffolder TaskPage (with run logs) for it.
 */

import { Container, FullPage } from '@backstage/ui';
import { BlueprintsHeader } from './BlueprintsHeader';
import CustomFieldsRegistry from './CustomFieldsRegistry';
import AuthoringGuide from './AuthoringGuide';

// ── Custom Fields page ───────────────────────────────────────────────────────

/**
 * Full-page wrapper for the Custom Fields tab.
 *
 * CustomFieldsRegistry renders its own `<Container>`, so we do NOT add
 * another one here — doing so would double-nest the padding.
 */
export function CustomFieldsPage(): JSX.Element {
  return (
    <FullPage>
      <BlueprintsHeader
        subtitle="Custom ui:field extensions available in Blueprint templates"
        pageTitleOverride="Blueprints – Custom Fields"
      />
      {/* CustomFieldsRegistry renders its own <Container>. */}
      <CustomFieldsRegistry />
    </FullPage>
  );
}

// ── Authoring Guide page ─────────────────────────────────────────────────────

/**
 * Full-page wrapper for the Authoring Guide tab.
 *
 * AuthoringGuide renders the embedded guide with no page container of its
 * own, so we add a `<Container>` here for correct page padding.
 */
export function AuthoringGuidePage(): JSX.Element {
  return (
    <FullPage>
      <BlueprintsHeader
        subtitle="How to write and contribute Blueprints templates"
        pageTitleOverride="Blueprints – Authoring Guide"
      />
      <Container py="6">
        <AuthoringGuide />
      </Container>
    </FullPage>
  );
}
